import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import { useRouter } from "next/router";
import { debounce } from "lodash";
import { Form, Field } from "react-final-form";
import TextInput from "fields/TextInput";
import Button from "components/Button";
import StorefrontIcon from "components/icons/storefront";
import LocationIcon from "components/icons/location";
import MagIcon from "components/icons/magglass";
import SelectDropdown from "fields/SelectDropdown";
import useVendorClient from "hooks/use-vendor-client";
import { BASE_API_URL } from "lib/constants";
import useOutsideClickHider from "components/helpers/OutsideClickHider";
import { loadMapsAPI } from "components/MapsApiWrapper";
import { DateTime } from "luxon";
import SearchRadius from "lib/search-radius";
import { parseDateToMsecs } from "lib/date-helpers";
import useCurrentLocation from "hooks/use-current-location";

const SearchForm = styled.form`
  display: grid;
  margin: 0 auto;
  row-gap: 1.125rem;
  width: 100%;
  column-gap: 1.25rem;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr;

  ${({ theme }) => theme.mq.md`
    row-gap: 1.375rem;
  `}

  ${({ theme }) => theme.mq.lg`
    row-gap: 3rem;
  `}
`;

export const StyledMagIcon = styled(MagIcon)`
  margin-right: 0.2rem;
`;

export const ListContainer = styled.div`
  margin: 0.625rem 0;
  border: 0.0625rem solid #eef5f5;
  box-sizing: border-box;
  box-shadow: 0 0.125rem 0.375rem rgba(138, 168, 181, 0.1253);
  position: absolute;
  width: calc(100% - 3.125rem);
  z-index: 1000;
  padding: 0.4rem 0.3rem;
  max-width: 26.875rem;
  max-height: 30rem;
  overflow: scroll;
  overflow-x: hidden;
  background-color: white;
  border-radius: 0.75rem;
  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    margin: 0.9375rem;
    box-shadow: none;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 1.25rem;
    height: 7.125rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.mq.lg`
    width: 100%;
  `}
`;

const ListHeader = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen3};
  text-transform: uppercase;
  font-size: 0.75rem;
  padding: 0.5rem 1rem;
`;

const LocationItem = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-weight: 400;
  padding: 0.5rem 1rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;

  &:hover, &.active {
    background-color: #F2FFF0;
  }

  svg {
    margin-top: -4px;
    margin-right: 6px;
  }
`;

export const LineDivide = styled.hr`
  opacity: 0.65;
  border: none;
  background-color: ${({ theme }) => theme.themeColors.blue4};
  height: 0.0625rem;
`;

const LocationText = styled.span`
  color: ${({ theme }) => theme.themeColors.darkGreen3};
  white-space:pre;
`;

const CapsLocationText = styled(LocationText)`
  text-transform: capitalize;
`;

export const SearchButton = styled(Button)`
  margin-top: 0.675rem;

  ${({ theme }) => theme.mq.lg`
    margin-top: -0.25rem;
  `}
`;

export const serviceTypeOptions = [
  {
    value: "all",
    label: "All services",
  },
  {
    value: "grooming",
    label: "Grooming",
  },
  {
    value: "training",
    label: "Training",
  },
  {
    value: "veterinary",
    label: "Veterinary",
  },
];

function UnifiedSearch({ theme, location, serviceType: serviceTypeParam }) {
  const vendorClient = useVendorClient();
  const router = useRouter();
  const { push } = useRouter();

  const [vendorBusinesses, setVendorBusinesses] = useState([]);
  const [places, setPlaces] = useState([]);
  const [placesService,setPlacesService] = useState(null);
  const [cursor, setCursor] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  let initialLocation = router?.query?.formattedAddress ? {
    formattedAddress: router?.query?.formattedAddress,
    latitude: router?.query?.latitude,
    longitude: router?.query?.longitude,
    radius: router?.query?.radius,
  } : null || location;

  const curLoc = useCurrentLocation();
  if (!initialLocation) {
    initialLocation = {
      formattedAddress: curLoc?.formatted_address,
      latitude: curLoc?.latitude,
      longitude: curLoc?.longitude,
      radius: 50,
    };
  }

  const [searchQuery, setSearchQuery] = useState(router.query.formattedAddress || router.query.q || initialLocation?.formattedAddress || '');
  const [serviceType, setServiceType] = useState(router.query.s || serviceTypeParam || 'all');
  const [selectedPlace, setSelectedPlace] = useState(initialLocation);

  const businessDropdownRef = React.useRef(null);
  useOutsideClickHider([businessDropdownRef], () => {
    setVendorBusinesses([]);
    setPlaces([]);
  });

  function getPlaces(searchString) {
    if (!searchString) {
      setPlaces([]);
      return null;
    }
    if (window.google?.maps) {
      const service  = new window.google.maps.places.AutocompleteService();
      return service.getPlacePredictions({ 
        input: searchString, 
        componentRestrictions: { country: "us" },
        types: ['geocode'],
      });
    }
    return null;
  }

  async function onChangeSearchHandler(e) {
    const searchString = e?.target?.value?.replace(/[^\w\s]/, '') ?? "";
    if (!searchString || searchString?.length < 2) {
      setVendorBusinesses([]);
      setPlaces([]);
      return;
    }

    const dataParams = {
      data: {
        data: {
          vendorBusinesses: {
            fields: [
              "businessName",
              "formattedAddress",
              "urlRegion",
              "urlLocality",
              "state",
              "city",
            ],
            filter: {
              strictSearchByBusinessName: searchString,
              restricted: false,
            },
            include: {
              vendorServices: {
                fields: ["name"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
            },
            paginate: { number: 1, size: 6 },
          },
        },
      },
    };

    const bizPromise = vendorClient
      .request({
        method: "get",
        url: `${BASE_API_URL}/deep-unrest/read`,
        params: dataParams,
      })
      .then(({ data }) => data?.vendorBusinesses?.data || []);

    const placesPromise = getPlaces(searchString)
      .then((result) => result.predictions || []);

    const bizResults = await bizPromise;
    const placesResults = await placesPromise;
    setVendorBusinesses(bizResults.slice(0,6));
    setPlaces(placesResults.slice(0,3));
  };

  const debouncedChangeSearchHandler = useCallback(
    debounce(onChangeSearchHandler, 300),
    []
  );

  useEffect(() => {
    return () => {
      // Stop the invocation of the debounced function after unmounting
      debouncedChangeSearchHandler.cancel();
    };
  }, []);

  useEffect(() => {
    loadMapsAPI(() => {
      if (window.google?.maps) {
        const map = new window.google.maps.Map(document.createElement("div"));
        const service = new window.google.maps.places.PlacesService(map);
        setPlacesService(service);
      }
    });
  }, []);

  function handlePlaceSelect(place) {
    if (!place) 
      return;
    setVendorBusinesses([]);
    setPlaces([]);
    setSearchQuery(place?.description);

    placesService?.getDetails({ placeId: place.place_id }, (detail, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !detail) {
        return;
      }

      const locality = detail?.address_components?.find((item) => {
        return item.types.includes("locality");
      })?.long_name;
      const region = detail?.address_components?.find((item) => {
        return item.types.includes("administrative_area_level_1");
      })?.short_name;

      const radius = SearchRadius(region, locality);

      const result = {
        formattedAddress: detail?.formatted_address,
        latitude: detail?.geometry?.location.lat(),
        longitude: detail?.geometry?.location.lng(),
        radius,
      };

      setSelectedPlace(result);
    });
  }

  function onSearchKeyDown(e) {
    if (e.key === "Enter") {
      // figure out what if anything is selected in the list
      if (cursor > 0) {
        e.preventDefault();
        if (places?.length > 0 && cursor <= places?.length) {
          setIsSubmitting(true);
          handlePlaceSelect(places?.[cursor-1]);
        } else if (vendorBusinesses?.length > 0) {
          push(`/business-redirect/${vendorBusinesses?.[cursor - places.length - 1]?.id}`);
        }
      } else if (places?.length > 0) {
        e.preventDefault();
        setIsSubmitting(true);
        handlePlaceSelect(places?.[0]);
      } else if (vendorBusinesses?.length > 0) {
        e.preventDefault();
        setIsSubmitting(true);
        push(`/business-redirect/${vendorBusinesses?.[0]?.id}`);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (cursor < (places.length + vendorBusinesses.length)) {
        setCursor(cursor+1);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cursor > 0) {
        setCursor(cursor-1);
      }
    }
  }

  function submitSearch() {
    const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
    const tomorrowMsecs = parseDateToMsecs(tomorrow);

    let query = {
      s: serviceType,
      visible: router?.query?.visible,
      startDateTime: tomorrowMsecs,
    };

    if (selectedPlace) {
      query = {
        ...query,
        ...selectedPlace,
      };
    } else {
      // this isn't showing up in the values param for some reason
      query.q = searchQuery;
    }

    push({
      pathname: "/search",
      query,
    });
  }

  useEffect(() => {
    if (isSubmitting) {
      submitSearch();
      setIsSubmitting(false);
    }
  }, [selectedPlace]);

  function highlightQuery(s) {
    const cleanQuery = searchQuery.replace(/[^\w\s]/, '')?.toLowerCase();
    const start = s.toLowerCase().indexOf(cleanQuery);
    const len = cleanQuery.length;

    const p0 = s.substring(0, start);
    const p1 = s.substring(start, start+len);
    const p2 = s.substring(start+len);

    return (
      <>
        {p0}<b>{p1}</b>{p2}
      </>
    );
  }

  return (
    <Form
      onSubmit={(e) => submitSearch(e)}
      initialValues={{
        serviceType,
        searchQuery,
      }}
      validate={(values) => {
        if (!values.searchQuery) {
          return { searchQuery: 'Please enter a search query' };
        }
        return {};
      }}
      render={({ handleSubmit, submitting }) => {
        return (
          <SearchForm>
            <div>
              <Field
                name="searchQuery"
                render={(props) => (
                  <TextInput
                    label="Search"
                    placeholder="Location or business name"
                    LeadingIcon={() => (
                      <StyledMagIcon color={theme.themeColors.darkGreen1} />
                    )}
                    {...props}
                    input={{
                      value: searchQuery,
                      onClick: (e) => e.target.select(),
                      onChange: (e) => {
                        setCursor(0);
                        setSearchQuery(e.target.value);
                        setSelectedPlace(null);
                        debouncedChangeSearchHandler(e);
                      },
                      onKeyDown: onSearchKeyDown,
                    }}
                  />
                )}
              />
              { vendorBusinesses?.length || places?.length ? (
                <ListContainer ref={businessDropdownRef}>
                  {!!places?.length && (
                    <>
                      {places && (<ListHeader>Locations</ListHeader>)}
                      {places?.map((place, i) => {
                        return (
                          <LocationItem 
                            key={place?.description?.replace(/\W*/g, '')}
                            className={cursor === i+1 ? 'active' : null}
                            onClick={() => {
                              handlePlaceSelect(place);
                            }}>
                            <LocationIcon color={theme.themeColors.darkGreen3} />
                            {highlightQuery(place.description)}
                          </LocationItem>
                        );
                      })}
                    </>
                  )}
                  { vendorBusinesses?.length && places?.length ? <LineDivide /> : null }
                  {!!vendorBusinesses?.length && (<>
                    <ListHeader>Businesses</ListHeader>
                    {vendorBusinesses?.map((biz, i) => {
                      return (
                        <LocationItem
                          key={biz?.attributes?.businessName?.replace(/\W*/g, '')}
                          className={cursor === (places?.length + i + 1) ? 'active' : null}
                          onClick={(e) => {
                            e.preventDefault();
                            push(`/business-redirect/${biz.id}`);
                          }}
                        >
                          <StorefrontIcon color={theme.themeColors.darkGreen3} />
                          {highlightQuery(biz?.attributes?.businessName)}
                          { biz?.attributes?.city && biz?.attributes?.state ? 
                            <LocationText> {biz?.attributes?.city}, {biz?.attributes?.state}</LocationText> :
                            <CapsLocationText> {biz?.attributes?.urlLocality}, {biz?.attributes?.urlRegion}</CapsLocationText>
                          }
                        </LocationItem>
                      );
                    })}
                  </>)}
                </ListContainer>
              ) : null}
            </div>
            <Field
              name="serviceType"
              initialValue={serviceType}
              render={(props) => (
                <SelectDropdown
                  highlightColor="green"
                  placeholder="Service"
                  options={serviceTypeOptions}
                  LeadingIcon={
                    <StorefrontIcon color={theme.themeColors.darkGreen1} />
                  }
                  {...props}
                  input={{
                    value: serviceType,
                    onChange: (value) => {
                      setServiceType(value);
                    }
                  }}
                />
              )}
            />
            <SearchButton
              disabled={submitting}
              onClick={(e) => handleSubmit(e)}
              role="button"
              type="submit"
            >
              Search
            </SearchButton>

          </SearchForm>
        );
      }}
    />
  );
}

UnifiedSearch.propTypes = {
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      darkGreen1: PropTypes.string,
      darkGreen3: PropTypes.string,
    })
  }),
  location: PropTypes.shape({
    formattedAddress: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    radius: PropTypes.number,
  }),
  serviceType: PropTypes.string,
};

UnifiedSearch.defaultProps = {
  theme: {},
  location: null,
  serviceType: 'all',
};

export default withTheme(UnifiedSearch);